import { getVariant } from "@/features/cart/utils/variants";
import type {
  DietCatalogCalorificsType,
  DietCatalogDetail,
} from "@/features/diets/types";
import type { DietVariant } from "@/features/diets/types/diet-variant.type";

const CALORICITY_NUMBER_REGEX = /\d+/;
const NUMBER_OF_MEALS_REGEX = /\d+/g;

const parseCaloricityParam = (value: string | undefined) => {
  if (!value) {
    return;
  }

  const match = value.match(CALORICITY_NUMBER_REGEX);

  if (!match) {
    return;
  }

  const parsed = Number(match[0]);

  if (!Number.isFinite(parsed)) {
    return;
  }

  return parsed;
};

const extractId = (atId?: string) => atId?.split("/").pop() || "";

const getCalorificId = (calorific: DietCatalogCalorificsType | undefined) =>
  calorific?.id || extractId(calorific?.["@id"]);

const getAvailableCalorifics = (
  calorifics: DietCatalogCalorificsType[] | undefined
) => {
  const list = calorifics ?? [];

  return list.flatMap((calorific) => {
    const id = getCalorificId(calorific);

    if (!id) {
      return [];
    }

    return [{ id, calories: calorific.calories }];
  });
};

const parseMealsCountsParam = (value: string | undefined) => {
  if (!value) {
    return;
  }

  const matches = value.match(NUMBER_OF_MEALS_REGEX);
  if (!matches) {
    return;
  }

  const parsed = matches
    .map((match) => Number(match))
    .filter((count) => Number.isFinite(count) && count > 0);

  if (parsed.length === 0) {
    return;
  }

  return Array.from(new Set(parsed));
};

const filterVariantsByMealsCounts = (
  variants: DietVariant[],
  mealsCounts: number[] | undefined
) => {
  if (!mealsCounts || mealsCounts.length === 0) {
    return variants;
  }

  const filtered = variants.filter((variant) =>
    mealsCounts.includes(variant.mealTypes.length)
  );

  return filtered.length > 0 ? filtered : variants;
};

const findNearestCalorific = (
  calorifics: DietCatalogCalorificsType[] | undefined,
  targetCalories: number
) => {
  const list = calorifics ?? [];

  if (list.length === 0) {
    return;
  }

  return list.reduce((closest, candidate) =>
    Math.abs(candidate.calories - targetCalories) <
    Math.abs(closest.calories - targetCalories)
      ? candidate
      : closest
  );
};

const getMealCount = (variant: DietVariant | undefined) =>
  variant?.mealTypes?.length ?? 0;

const isAllowedMeals = (
  variant: DietVariant | undefined,
  maxAllowedMeals: number
) => getMealCount(variant) < maxAllowedMeals;

const pickVariantWithMostMeals = (variants: DietVariant[]) => {
  if (variants.length === 0) {
    return;
  }

  return variants.reduce((bestVariant, currentVariant) =>
    getMealCount(currentVariant) > getMealCount(bestVariant)
      ? currentVariant
      : bestVariant
  );
};

const getNearestDistanceToTarget = (
  calorifics: DietCatalogCalorificsType[] | undefined,
  targetCalories: number
) => {
  const list = calorifics ?? [];

  if (list.length === 0) {
    return Number.POSITIVE_INFINITY;
  }

  return list.reduce((bestDistance, calorific) => {
    const distance = Math.abs(calorific.calories - targetCalories);
    return distance < bestDistance ? distance : bestDistance;
  }, Number.POSITIVE_INFINITY);
};

const pickBetterVariantForTarget = (
  bestVariant: DietVariant,
  currentVariant: DietVariant,
  targetCalories: number,
  maxAllowedMeals: number
) => {
  const bestDistance = getNearestDistanceToTarget(
    bestVariant?.calorifics,
    targetCalories
  );
  const currentDistance = getNearestDistanceToTarget(
    currentVariant?.calorifics,
    targetCalories
  );

  if (currentDistance < bestDistance) {
    return currentVariant;
  }

  if (currentDistance > bestDistance) {
    return bestVariant;
  }

  const bestAllowed = isAllowedMeals(bestVariant, maxAllowedMeals);
  const currentAllowed = isAllowedMeals(currentVariant, maxAllowedMeals);

  if (currentAllowed && !bestAllowed) {
    return currentVariant;
  }

  if (!currentAllowed && bestAllowed) {
    return bestVariant;
  }

  return getMealCount(currentVariant) > getMealCount(bestVariant)
    ? currentVariant
    : bestVariant;
};

const pickVariantClosestToTarget = (
  variants: DietVariant[],
  targetCalories: number,
  maxAllowedMeals: number
) => {
  if (variants.length === 0) {
    return;
  }

  return variants.reduce((bestVariant, currentVariant) =>
    pickBetterVariantForTarget(
      bestVariant,
      currentVariant,
      targetCalories,
      maxAllowedMeals
    )
  );
};

const resolveEffectiveVariant = ({
  data,
  variant,
  numberOfMeals,
  targetKcalFromParams,
  maxAllowedMeals,
}: {
  data: DietCatalogDetail | undefined;
  variant: string | undefined;
  numberOfMeals: string | undefined;
  targetKcalFromParams: number | undefined;
  maxAllowedMeals: number;
}) => {
  const variants: DietVariant[] = data?.variants ?? [];
  const selectedVariant = getVariant(data, variant);

  const mealsCounts = parseMealsCountsParam(numberOfMeals);
  const candidateVariants = filterVariantsByMealsCounts(variants, mealsCounts);

  if (selectedVariant) {
    return { variants: candidateVariants, effectiveVariant: selectedVariant };
  }

  if (candidateVariants.length === 0) {
    return { variants: candidateVariants, effectiveVariant: undefined };
  }

  if (targetKcalFromParams !== undefined) {
    const closest = pickVariantClosestToTarget(
      candidateVariants,
      targetKcalFromParams,
      maxAllowedMeals
    );
    if (closest) {
      return { variants: candidateVariants, effectiveVariant: closest };
    }
  }

  const allowedVariants = candidateVariants.filter((v) =>
    isAllowedMeals(v, maxAllowedMeals)
  );
  const defaultVariant =
    pickVariantWithMostMeals(allowedVariants) ??
    pickVariantWithMostMeals(candidateVariants);

  return { variants: candidateVariants, effectiveVariant: defaultVariant };
};

const resolveFallbackCalorific = ({
  data,
  variants,
  effectiveVariant,
  targetKcalFromParams,
  targetDefaultCalories,
}: {
  data: DietCatalogDetail | undefined;
  variants: DietVariant[];
  effectiveVariant: DietVariant | undefined;
  targetKcalFromParams: number | undefined;
  targetDefaultCalories: number;
}) => {
  if (targetKcalFromParams !== undefined) {
    return;
  }

  if (variants.length > 0) {
    return findNearestCalorific(
      effectiveVariant?.calorifics,
      targetDefaultCalories
    );
  }

  return findNearestCalorific(data?.calorifics, targetDefaultCalories);
};

export const getCalorific = ({
  data,
  variant,
  numberOfMeals,
  kcal,
  targetDefaultCalories,
  maxAllowedMeals,
}: {
  data: DietCatalogDetail | undefined;
  variant: string | undefined;
  numberOfMeals: string | undefined;
  kcal: string | undefined;
  targetDefaultCalories: number;
  maxAllowedMeals: number;
}) => {
  const targetKcalFromParams = parseCaloricityParam(kcal);

  const { variants, effectiveVariant } = resolveEffectiveVariant({
    data,
    variant,
    numberOfMeals,
    targetKcalFromParams,
    maxAllowedMeals,
  });

  const fallbackCalorific = resolveFallbackCalorific({
    data,
    variants,
    effectiveVariant,
    targetKcalFromParams,
    targetDefaultCalories,
  });

  const targetKcal =
    targetKcalFromParams ??
    fallbackCalorific?.calories ??
    targetDefaultCalories;

  const calorifics =
    variants.length > 0 ? effectiveVariant?.calorifics : data?.calorifics;
  const exact = calorifics?.find((c) => c.calories === targetKcal);
  const selectedCalorific =
    exact ?? findNearestCalorific(calorifics, targetKcal);

  return {
    id: getCalorificId(selectedCalorific),
    calories: selectedCalorific?.calories ?? targetKcal,
    targetKcal,
    availableCalorifics: getAvailableCalorifics(calorifics),
  };
};
